import { LiItem } from "@faire/help-center-self-serve/brand/instantAnswers/instantAnswersComponents/common/LiItem";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { Bold } from "@faire/web--source/ui/Typography/Span";
import userAccountOrders from "@faire/web-api--source/routes/user/account/orders";
import React, { type JSX } from "react";

export const CancelOrderIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="You can cancel an order on Faire if the brand hasn’t started processing it. If processing has begun, you can request a cancellation, and the brand can accept or decline. If an order is canceled unexpectedly, check your email for details or contact the brand directly."
          description={{
            img: "https://cdn.faire.com/fastly/c31c97fae93e0bc89d64cea30c20493642ca7e483da37c2c557efac5ed1a5730.png",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="Orders can’t be canceled once they’ve shipped."
          description={{
            img: "https://cdn.faire.com/fastly/c31c97fae93e0bc89d64cea30c20493642ca7e483da37c2c557efac5ed1a5730.png",
          }}
        />
      </Typography>
      <Spacer height="2x" />
      <Typography>
        <Bold>
          <StrictLocalMsg
            defaultMessage="To cancel an order:"
            description={{
              img: "https://cdn.faire.com/fastly/c31c97fae93e0bc89d64cea30c20493642ca7e483da37c2c557efac5ed1a5730.png",
            }}
          />
        </Bold>
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Go to your <OrderLinkFormatting>Orders</OrderLinkFormatting>"
              description={{
                img: "https://cdn.faire.com/fastly/c31c97fae93e0bc89d64cea30c20493642ca7e483da37c2c557efac5ed1a5730.png",
              }}
              values={{
                OrderLinkFormatting,
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Find the order you want to cancel and select <BoldFormatting>Cancel items</BoldFormatting>"
              description={{
                img: "https://cdn.faire.com/fastly/c31c97fae93e0bc89d64cea30c20493642ca7e483da37c2c557efac5ed1a5730.png",
              }}
              values={{
                BoldFormatting,
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="In the pop-up, select a cancellation reason and add a note if needed."
              description={{
                img: "https://cdn.faire.com/fastly/c31c97fae93e0bc89d64cea30c20493642ca7e483da37c2c557efac5ed1a5730.png",
              }}
              values={{
                BoldFormatting,
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select <BoldFormatting>Cancel Order</BoldFormatting> to confirm."
              description={{
                img: "https://cdn.faire.com/fastly/c31c97fae93e0bc89d64cea30c20493642ca7e483da37c2c557efac5ed1a5730.png",
              }}
              values={{
                BoldFormatting,
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="2x" />
      <Typography>
        <Bold>
          <StrictLocalMsg
            defaultMessage="To request a cancellation:"
            description={{
              img: "https://cdn.faire.com/fastly/c31c97fae93e0bc89d64cea30c20493642ca7e483da37c2c557efac5ed1a5730.png",
            }}
          />
        </Bold>
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Go to your <OrderLinkFormatting>Orders</OrderLinkFormatting>"
              description={{
                img: "https://cdn.faire.com/fastly/c31c97fae93e0bc89d64cea30c20493642ca7e483da37c2c557efac5ed1a5730.png",
              }}
              values={{
                OrderLinkFormatting,
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Find the order and select <BoldFormatting>Request cancellation</BoldFormatting>."
              description={{
                img: "https://cdn.faire.com/fastly/c31c97fae93e0bc89d64cea30c20493642ca7e483da37c2c557efac5ed1a5730.png",
              }}
              values={{
                BoldFormatting,
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="In the pop-up, choose a reason and add a note if desired."
              description={{
                img: "https://cdn.faire.com/fastly/c31c97fae93e0bc89d64cea30c20493642ca7e483da37c2c557efac5ed1a5730.png",
              }}
              values={{
                BoldFormatting,
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select <BoldFormatting>Request cancellation</BoldFormatting> to send to the brand."
              description={{
                img: "https://cdn.faire.com/fastly/c31c97fae93e0bc89d64cea30c20493642ca7e483da37c2c557efac5ed1a5730.png",
              }}
              values={{
                BoldFormatting,
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="2x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="The brand will have 2 days to respond. If they don’t respond, the request is automatically declined. "
          description={{
            img: "https://cdn.faire.com/fastly/c31c97fae93e0bc89d64cea30c20493642ca7e483da37c2c557efac5ed1a5730.png",
          }}
        />
      </Typography>
    </Column>
  );
};

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;

const OrderLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={userAccountOrders()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);
